import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useTranslations } from 'hooks/useTranslations';
import { EPrintoutslabels, initialPrintoutsSearchForm } from '../helpers';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { FormInput } from 'components/molecules/FormInput';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { StyledSearchForm } from 'theme/styles';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { prepareFilters } from 'helpers/filters';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { IPrintoutsSearchForm } from 'models/licences';
import { useDictionary } from 'models/dictionaryContext';
import { FormDateTimePicker } from 'components/molecules/FormDateTimePicker';
import { useAccount } from 'pages/AccountPage/hooks';

interface IApplicationsSearchForm {
  onSubmit: (arg: IPrintoutsSearchForm) => void;
}

const PrintoutsSearchForm = ({ onSubmit }: IApplicationsSearchForm) => {
  const { t } = useTranslations();
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [values, setValues] = useState<IPrintoutsSearchForm>(initialPrintoutsSearchForm);
  const { isDesktop } = useThemeBreakpoint();
  const { account, getSelfAccountData } = useAccount();

  useEffect(() => {
    getSelfAccountData();
  }, []);

  const {
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    weaponLicencePrintStatus,
    getWeaponLicencePrintStatus,
  } = useDictionary();
  useEffect(() => {
    if (!weaponLicencePrintStatus) {
      getWeaponLicencePrintStatus();
    }
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
  }, []);

  useEffect(() => {
    setValues(initialPrintoutsSearchForm);
    reset(initialPrintoutsSearchForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IPrintoutsSearchForm>({
    defaultValues: initialPrintoutsSearchForm,
  });

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as IPrintoutsSearchForm);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const licenceAuthorityUniqueIds = watch('licenceAuthorityUniqueIds');

  useEffect(() => {
    if (weaponLicencingAuthority && !licenceAuthorityUniqueIds.length && account) {
      const values = weaponLicencingAuthority
        .filter((licencingAuthority) =>
          account?.licenceAuthorityIds.includes(licencingAuthority.uniqueId.toString()),
        )
        .map((item) => item.uniqueId.toString());

      setValue('licenceAuthorityUniqueIds', values);
      onSubmit(getValues());
    }
  }, [weaponLicencingAuthority, account]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      if (['statusUniqueIds', 'licenceAuthorityUniqueIds'].includes(name)) {
        reset({ ...getValues(), [name]: [] });
      } else {
        reset({ ...getValues(), [name]: '' });
      }
    } else {
      reset(initialPrintoutsSearchForm);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          size={{ xs: 12 }}
          container
          sx={{
            alignItems: 'center',
            flexWrap: { md: 'nowrap', xs: 'wrap' },
            rowGap: 2,
            mb: 2,
          }}
          columnSpacing={2}
        >
          <Grid
            size={{ xs: 12 }}
            container
            sx={{ justifyContent: 'flex-start', flexWrap: 'nowrap', gap: 0 }}
          >
            <FormInput
              charsLimit={120}
              maxLength
              InputProps={{
                style: {
                  height: '40px',
                  margin: 0,
                  width: isDesktop ? '330px' : 'auto',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root fieldset': {
                  borderEndEndRadius: 0,
                  borderStartEndRadius: 0,
                },
              }}
              withValidation={false}
              name="search"
              withLabel={false}
              placeholder={t('global.search.button')}
              control={control}
              setValue={setValue}
              errors={errors}
              fullWidth={!isDesktop}
              id="printouts_search_form_search-input"
            />
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.medium}
              label={t('global.search.button')}
              type="submit"
              sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              id="printouts_search_form_search-button"
            />
            <Button
              variant={EButtonVariants.outlined}
              sizePX={EButtonSizesPX.px40}
              icon={EIconTypes.filters2}
              type="submit"
              sx={{ ml: '20px' }}
              onClick={() => setShowAdvanced(!showAdvanced)}
              id="printouts_search_form_filters2-button"
            />
          </Grid>
        </Grid>
        {showAdvanced && (
          <SearchWrapper>
            <StyledSearchForm
              container
              sx={{ justifyContent: 'space-between' }}
              columnSpacing={2}
            >
              <Grid size={{ xs: 12, md: 3 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                  name="licenceAuthorityUniqueIds"
                  label={t(EPrintoutslabels.licenceAuthorityUniqueIds)}
                  canBeEmpty
                  searchable={false}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormInput
                  name="licenceOwner"
                  setValue={setValue}
                  label={t(EPrintoutslabels.licenceOwner)}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormDateTimePicker
                  name="requestDateFrom"
                  label={t(EPrintoutslabels.requestDateFrom)}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormDateTimePicker
                  name="requestDateTo"
                  label={t(EPrintoutslabels.requestDateTo)}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(weaponLicencePrintStatus)}
                  name="statusUniqueIds"
                  label={t(EPrintoutslabels.statusUniqueIds)}
                  canBeEmpty
                  searchable={false}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Button
                  variant={EButtonVariants.contained}
                  size={EButtonSizes.small}
                  label={t('global.search.button')}
                  type="submit"
                  fullWidth
                  alignWithInputSX
                  id="case_search_form_search-button"
                />
              </Grid>
            </StyledSearchForm>
            <SelectedFilters
              name="printouts"
              values={values}
              excludes={['perPage', 'currentPage', 'search']}
              labels={EPrintoutslabels}
              handleDelete={handleResetField}
              showSaveFilters={false}
              saveRouterStateAfterClearAll={false}
              dictionaries={{
                statusUniqueIds: prepareFilters(weaponLicencePrintStatus, 'uniqueId', 'name'),
                licenceAuthorityUniqueIds: prepareFilters(
                  weaponLicencingAuthority,
                  'uniqueId',
                  'name',
                ),
              }}
            />
          </SearchWrapper>
        )}
      </form>
    </div>
  );
};

export { PrintoutsSearchForm };
